import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Tag, Hash, Box, DollarSign, Warehouse, Calendar, Save, XCircle, PackagePlus, 
  Package, ArrowLeft, Camera, Upload, QrCode, Printer, Download
} from 'lucide-react';

const InventoryForm = ({ currentProduct, onSave, onCancel }) => {
  const [product, setProduct] = useState({
    id: null,
    name: '',
    code: '',
    sku: '',
    category: '',
    subcategory: '',
    brand: '',
    model: '',
    stock: '',
    unit: 'piezas',
    price: '',
    cost_price: '',
    location: '',
    min_stock: '',
    max_stock: '',
    description: '',
    weight: '',
    dimensions: '',
    barcode: '',
    supplier_id: '',
    is_active: true
  });

  useEffect(() => {
    if (currentProduct) {
      setProduct({
        ...currentProduct,
        stock: currentProduct.stock || currentProduct.quantity || '',
        unit: currentProduct.unit || 'piezas'
      });
    } else {
      setProduct({
        id: null,
        name: '',
        code: '',
        sku: '',
        category: '',
        subcategory: '',
        brand: '',
        model: '',
        stock: '',
        unit: 'piezas',
        price: '',
        cost_price: '',
        location: '',
        min_stock: '',
        max_stock: '',
        description: '',
        weight: '',
        dimensions: '',
        barcode: '',
        supplier_id: '',
        is_active: true
      });
    }
  }, [currentProduct]);

  const handleChange = (field, value) => {
    setProduct(prevProduct => ({
      ...prevProduct,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mapear los campos correctamente para el backend
    const productData = {
      name: product.name,
      code: product.code || product.sku,
      sku: product.sku || product.code,
      category: product.category,
      subcategory: product.subcategory,
      brand: product.brand,
      model: product.model,
      // Mapeo correcto para backend: current_stock
      current_stock: parseInt(product.stock) || 0,
      // Mantener campos legacy por compatibilidad en otros componentes
      stock: parseInt(product.stock) || 0,
      quantity: parseInt(product.stock) || 0,
      unit: product.unit || 'piezas',
      price: product.price,
      cost_price: product.cost_price,
      location: product.location,
      min_stock: product.min_stock,
      max_stock: product.max_stock,
      description: product.description,
      weight: product.weight,
      dimensions: product.dimensions,
      barcode: product.barcode,
      supplier_id: product.supplier_id,
      is_active: product.is_active
    };
    
    if (currentProduct && currentProduct.id) {
      productData.id = currentProduct.id;
    }
    
    onSave(productData);
  };

  // Función para calcular el porcentaje de stock
  const getStockPercentage = () => {
    const currentStock = parseFloat(product.stock) || 0;
    const maxStock = parseFloat(product.max_stock) || 100;
    return Math.min(100, Math.max(0, (currentStock / maxStock) * 100));
  };

  // Función para determinar el color del stock
  const getStockColor = () => {
    const currentStock = parseFloat(product.stock) || 0;
    const minStock = parseFloat(product.min_stock) || 0;
    const maxStock = parseFloat(product.max_stock) || 0;
    
    if (currentStock <= minStock) return 'bg-red-500';
    if (currentStock >= maxStock) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <motion.button
            onClick={onCancel}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al Inventario
          </motion.button>
        </div>

        {/* Título principal */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-extrabold text-gray-800 mb-2 bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent">
            {currentProduct ? 'Editar Producto' : 'Añadir Nuevo Producto'}
          </h1>
          <p className="text-gray-600">
            {currentProduct ? 'Modifica los datos de tu refacción' : 'Introduce los datos de la nueva refacción'}
          </p>
        </motion.div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Columna izquierda - Imagen y código de barras */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-1 space-y-6"
            >
              {/* Imagen del producto */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Camera className="w-5 h-5 text-blue-600" />
                  Imagen del Producto
                </h3>
                
                <div className="relative bg-gray-50 flex items-center justify-center">
                  <div className="w-full h-64 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <Package className="w-12 h-12 mx-auto mb-2" />
                      <p>Sin imagen</p>
                      <p className="text-xs">Disponible después de guardar</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Código de barras preview */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-blue-600" />
                  Código de Barras
                </h3>
                
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <div className="h-20 bg-gray-200 rounded flex items-center justify-center mb-2">
                    <span className="text-gray-500 text-sm">Código de barras generado automáticamente</span>
                  </div>
                  <p className="text-sm text-gray-600">SKU: {product.sku || 'Se generará automáticamente'}</p>
                </div>
              </div>
            </motion.div>

            {/* Columna derecha - Información detallada */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-2 space-y-6"
            >
              {/* Información básica del producto */}
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                  <Package className="w-6 h-6 text-blue-600" />
                  Información Básica
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    {/* Nombre del producto */}
                    <div className="flex items-center gap-3">
                      <Tag className="w-5 h-5 text-gray-500" />
                      <div className="flex-1">
                        <label className="block text-sm text-gray-500 mb-1">Nombre del Producto *</label>
                        <input
                          type="text"
                          value={product.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          className="w-full font-semibold text-lg bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Ingresa el nombre del producto"
                          required
                        />
                      </div>
                    </div>

                    {/* Código */}
                    <div className="flex items-center gap-3">
                      <Hash className="w-5 h-5 text-gray-500" />
                      <div className="flex-1">
                        <label className="block text-sm text-gray-500 mb-1">Código</label>
                        <input
                          type="text"
                          value={product.code}
                          onChange={(e) => handleChange('code', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Código interno"
                        />
                      </div>
                    </div>

                    {/* SKU */}
                    <div className="flex items-center gap-3">
                      <Tag className="w-5 h-5 text-gray-500" />
                      <div className="flex-1">
                        <label className="block text-sm text-gray-500 mb-1">SKU *</label>
                        <input
                          type="text"
                          value={product.sku}
                          onChange={(e) => handleChange('sku', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Código SKU único"
                          required
                        />
                      </div>
                    </div>

                    {/* Categoría */}
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-gray-500" />
                      <div className="flex-1">
                        <label className="block text-sm text-gray-500 mb-1">Categoría</label>
                        <input
                          type="text"
                          value={product.category}
                          onChange={(e) => handleChange('category', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Categoría del producto"
                        />
                      </div>
                    </div>

                    {/* Subcategoría */}
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-gray-500" />
                      <div className="flex-1">
                        <label className="block text-sm text-gray-500 mb-1">Subcategoría</label>
                        <input
                          type="text"
                          value={product.subcategory}
                          onChange={(e) => handleChange('subcategory', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Subcategoría"
                        />
                      </div>
                    </div>

                    {/* Marca */}
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-gray-500" />
                      <div className="flex-1">
                        <label className="block text-sm text-gray-500 mb-1">Marca</label>
                        <input
                          type="text"
                          value={product.brand}
                          onChange={(e) => handleChange('brand', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Marca del producto"
                        />
                      </div>
                    </div>

                    {/* Modelo */}
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-gray-500" />
                      <div className="flex-1">
                        <label className="block text-sm text-gray-500 mb-1">Modelo</label>
                        <input
                          type="text"
                          value={product.model}
                          onChange={(e) => handleChange('model', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Modelo del producto"
                        />
                      </div>
                    </div>

                    {/* Peso */}
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-gray-500" />
                      <div className="flex-1">
                        <label className="block text-sm text-gray-500 mb-1">Peso</label>
                        <input
                          type="text"
                          value={product.weight}
                          onChange={(e) => handleChange('weight', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="ej: 1.5 kg"
                        />
                      </div>
                    </div>

                    {/* Dimensiones */}
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-gray-500" />
                      <div className="flex-1">
                        <label className="block text-sm text-gray-500 mb-1">Dimensiones</label>
                        <input
                          type="text"
                          value={product.dimensions}
                          onChange={(e) => handleChange('dimensions', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="ej: 10x20x30 cm"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* Stock Actual */}
                    <div className="flex items-center gap-3">
                      <Warehouse className="w-5 h-5 text-gray-500" />
                      <div className="flex-1">
                        <label className="block text-sm text-gray-500 mb-1">Stock Actual *</label>
                        <input
                          type="number"
                          value={product.stock}
                          onChange={(e) => handleChange('stock', e.target.value)}
                          className="w-full font-semibold text-2xl text-blue-600 bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0"
                          min="0"
                          required
                        />
                      </div>
                    </div>

                    {/* Unidad */}
                    <div className="flex items-center gap-3">
                      <Box className="w-5 h-5 text-gray-500" />
                      <div className="flex-1">
                        <label className="block text-sm text-gray-500 mb-1">Unidad</label>
                        <select
                          value={product.unit}
                          onChange={(e) => handleChange('unit', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="piezas">Piezas</option>
                          <option value="metros">Metros</option>
                          <option value="litros">Litros</option>
                          <option value="kilogramos">Kilogramos</option>
                          <option value="cajas">Cajas</option>
                          <option value="paquetes">Paquetes</option>
                          <option value="unidades">Unidades</option>
                        </select>
                      </div>
                    </div>

                    {/* Precio de Venta */}
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-5 h-5 text-gray-500" />
                      <div className="flex-1">
                        <label className="block text-sm text-gray-500 mb-1">Precio de Venta *</label>
                        <input
                          type="number"
                          value={product.price}
                          onChange={(e) => handleChange('price', e.target.value)}
                          className="w-full font-semibold text-lg text-green-600 bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                    </div>

                    {/* Precio de Costo */}
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-5 h-5 text-gray-500" />
                      <div className="flex-1">
                        <label className="block text-sm text-gray-500 mb-1">Precio de Costo</label>
                        <input
                          type="number"
                          value={product.cost_price}
                          onChange={(e) => handleChange('cost_price', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>

                    {/* Ubicación */}
                    <div className="flex items-center gap-3">
                      <Warehouse className="w-5 h-5 text-gray-500" />
                      <div className="flex-1">
                        <label className="block text-sm text-gray-500 mb-1">Ubicación</label>
                        <input
                          type="text"
                          value={product.location}
                          onChange={(e) => handleChange('location', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Ubicación en almacén"
                        />
                      </div>
                    </div>

                    {/* Control de Stock */}
                    <div className="bg-gray-50 p-4 rounded-lg border">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Control de Stock</h4>
                      <div className="space-y-3">
                        {/* Barra de progreso de stock */}
                        <div className="relative">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>Mín: {product.min_stock || 0}</span>
                            <span>Actual: {product.stock || 0}</span>
                            <span>Máx: {product.max_stock || 0}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full transition-all duration-300 ${getStockColor()}`}
                              style={{ width: `${getStockPercentage()}%` }}
                            ></div>
                          </div>
                        </div>
                        
                        {/* Edición de stock mínimo y máximo */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Stock Mínimo</label>
                            <input
                              type="number"
                              value={product.min_stock}
                              onChange={(e) => handleChange('min_stock', e.target.value)}
                              className="w-full text-sm bg-white border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="0"
                              min="0"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Stock Máximo</label>
                            <input
                              type="number"
                              value={product.max_stock}
                              onChange={(e) => handleChange('max_stock', e.target.value)}
                              className="w-full text-sm bg-white border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="100"
                              min="0"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Descripción */}
                <div className="mt-6">
                  <label className="block text-sm text-gray-500 mb-2">Descripción</label>
                  <textarea
                    value={product.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Descripción detallada del producto..."
                  />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Botones de acción */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center gap-4 mt-8"
          >
            <motion.button
              type="button"
              onClick={onCancel}
              className="flex items-center gap-2 px-8 py-3 rounded-xl font-semibold text-lg bg-gray-200 text-gray-700 shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <XCircle className="w-5 h-5" />
              Cancelar
            </motion.button>
            <motion.button
              type="submit"
              className="flex items-center gap-2 px-8 py-3 rounded-xl font-semibold text-lg bg-gradient-to-r from-green-600 to-teal-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <Save className="w-5 h-5" />
              {currentProduct ? 'Guardar Cambios' : 'Añadir Producto'}
            </motion.button>
          </motion.div>
        </form>
      </div>
    </div>
  );
};

export default InventoryForm;